import React, { useState } from "react";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import useRLContext from "@/hooks/useRLContext";
import RLShopServiceModeBadge from "@/modules/racket_lab_admin/RLShopServiceModeBadge";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLShopSettingsRequestCard — RACKET-LAB-ROLE-5 (Feb 2026).
 *
 * Inline card mounted in `RLAdminShopDetail` that surfaces:
 *
 *  - the shop's current `service_mode` as a visible badge;
 *  - a "Request a settings change" inline form for shop_manager (and
 *    admin/staff) — submits a record to
 *    `POST /admin/racket-lab/shop-settings-requests`; ZERO real
 *    notifications fire;
 *  - the list of pending requests on this shop with their state
 *    (pending / approved / rejected / cancelled) plus a decide action
 *    for admin/staff and a cancel action for the requester.
 *
 * NO direct mutation of the shop settings happens here — the existing
 * PATCH /admin/racket-lab/shops/{id} endpoint stays the source of
 * truth for admin/staff. This card is a coordination registry only.
 *
 * No customer-facing surface. No real notifications. No migrations.
 */
const RLShopSettingsRequestCard = ({ shop, onShopReload }) => {
  const t = useRacketLabAdminT();
  const { ctx } = useRLContext();
  const profile = ctx?.profile;
  const canRequest = profile === "admin" || profile === "staff" || profile === "shop_manager";
  const canDecide = profile === "admin" || profile === "staff";
  const { run, loading: mutating } = useAdminMutation();
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);

  const { data: list, loading, reload } = useAdminApi(
    "/admin/racket-lab/shop-settings-requests",
    { skip: !shop?.id, params: { racket_lab_shop_id: shop?.id, limit: 20 } },
  );
  const items = list?.items || [];
  const mode = shop?.settings?.service_mode;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    try {
      const ok = await run("post", "/admin/racket-lab/shop-settings-requests", {
        racket_lab_shop_id: shop.id,
        reason: reason.trim(),
      });
      if (ok) {
        setReason("");
        setOpen(false);
        reload();
      }
    } catch { /* toast already shown by useAdminMutation */ }
  };

  const decide = async (id, decision) => {
    try {
      const ok = await run("patch", `/admin/racket-lab/shop-settings-requests/${id}/decision`, {
        decision,
      });
      if (ok) {
        reload();
        onShopReload?.();
      }
    } catch { /* toast already shown */ }
  };

  const cancel = async (id) => {
    try {
      const ok = await run("patch", `/admin/racket-lab/shop-settings-requests/${id}/cancel`, {});
      if (ok) reload();
    } catch { /* toast already shown */ }
  };

  return (
    <section
      data-testid="rl-shop-settings-request-card"
      className="rounded-md border border-slate-200 bg-white p-4 md:col-span-2"
    >
      <header className="mb-3 flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-slate-800">
            {t("shops.detail.service_mode_section_title")}
          </h3>
          <RLShopServiceModeBadge mode={mode} />
        </div>
        {canRequest && (
          <button
            type="button"
            data-testid="rl-shop-settings-request-open"
            onClick={() => setOpen((v) => !v)}
            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
          >
            {open ? t("shops.detail.cancel") : t("shops.detail.request_change_btn")}
          </button>
        )}
      </header>

      <p className="mb-3 text-[11px] text-slate-500">
        {t("shops.detail.request_change_hint")}
      </p>

      {open && canRequest && (
        <form
          data-testid="rl-shop-settings-request-form"
          onSubmit={onSubmit}
          className="mb-4 grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 md:grid-cols-[1fr_auto]"
        >
          <textarea
            data-testid="rl-shop-settings-request-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
            rows={2}
            required
            placeholder={t("shops.detail.request_change_placeholder")}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs"
          />
          <button
            type="submit"
            data-testid="rl-shop-settings-request-submit"
            disabled={mutating || !reason.trim()}
            className="self-end rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {mutating ? t("shops.detail.submitting") : t("shops.detail.submit_request")}
          </button>
        </form>
      )}

      <div data-testid="rl-shop-settings-request-list">
        <p className="mb-2 text-[10px] uppercase tracking-wide text-slate-500">
          {t("shops.detail.requests_history")}
        </p>
        {loading && <p className="text-xs text-slate-500">{t("shops.detail.loading_list")}</p>}
        {!loading && items.length === 0 && (
          <p data-testid="rl-shop-settings-request-empty" className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            {t("shops.detail.requests_empty")}
          </p>
        )}
        {!loading && items.length > 0 && (
          <ul className="space-y-2">
            {items.map((r) => (
              <li
                key={r.id}
                data-testid={`rl-shop-settings-request-row-${r.id}`}
                className="rounded-md border border-slate-200 bg-white p-2 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    data-testid={`rl-shop-settings-request-status-${r.id}`}
                    className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                      r.status === "pending" ? "bg-amber-100 text-amber-900" :
                      r.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                      r.status === "rejected" ? "bg-rose-100 text-rose-800" :
                      "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {t(`shops.detail.req_status_${r.status}`)}
                  </span>
                  <span className="text-[9px] text-slate-500">
                    {r.requested_by_role} · {r.created_at?.slice(0, 16).replace("T", " ")}
                  </span>
                </div>
                <p className="mt-1 text-slate-700">{r.reason}</p>
                {r.decision_reason && (
                  <p className="mt-1 text-[10px] text-slate-500">
                    {t("shops.detail.decision_reason")}: {r.decision_reason}
                  </p>
                )}
                {r.status === "pending" && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {canDecide && (
                      <>
                        <button
                          type="button"
                          data-testid={`rl-shop-settings-request-approve-${r.id}`}
                          onClick={() => decide(r.id, "approved")}
                          disabled={mutating}
                          className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed"
                        >
                          {t("shops.detail.approve")}
                        </button>
                        <button
                          type="button"
                          data-testid={`rl-shop-settings-request-reject-${r.id}`}
                          onClick={() => decide(r.id, "rejected")}
                          disabled={mutating}
                          className="rounded bg-rose-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed"
                        >
                          {t("shops.detail.reject")}
                        </button>
                      </>
                    )}
                    {canRequest && (
                      <button
                        type="button"
                        data-testid={`rl-shop-settings-request-cancel-${r.id}`}
                        onClick={() => cancel(r.id)}
                        disabled={mutating}
                        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed"
                      >
                        {t("shops.detail.cancel_request")}
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default RLShopSettingsRequestCard;
